import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import AiScoreBadge from "../../components/AiScoreBadge";
import StatusBadge from "../../components/StatusBadge";
import { fmtDate, LOAN_TYPE_LABELS } from "../../lib/constants";
import { Eye, Send } from "lucide-react";
import { useT } from "../../lib/i18n";
import { toast } from "sonner";

export default function SponsorApplications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const { t } = useT();

  const load = async () => {
    try {
      const { data } = await api.get("/sponsor/applications");
      setApps(data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const requestDocs = async (id) => {
    try {
      await api.post(`/sponsor/applications/${id}/request-docs`);
      toast.success("Documents requested from borrower");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t("sponsor.applications.title")}</h1>
        <p className="mt-1 text-sm text-slate-400">{t("sponsor.applications.subtitle")}</p>
      </div>

      <Card className="border-slate-800 bg-slate-900" data-testid="applications-card">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="text-slate-400">{t("sponsor.applications.borrower")}</TableHead>
              <TableHead className="text-slate-400">{t("sponsor.applications.property")}</TableHead>
              <TableHead className="text-slate-400">{t("sponsor.applications.loanType")}</TableHead>
              <TableHead className="text-slate-400">{t("sponsor.applications.aiScore")}</TableHead>
              <TableHead className="text-slate-400">{t("common.status")}</TableHead>
              <TableHead className="text-slate-400">{t("common.source")}</TableHead>
              <TableHead className="text-slate-400">{t("common.date")}</TableHead>
              <TableHead className="text-slate-400 text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow className="border-slate-800"><TableCell colSpan={8} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && apps.length === 0 && (
              <TableRow className="border-slate-800"><TableCell colSpan={8} className="text-center text-slate-500 py-8">No applications yet.</TableCell></TableRow>
            )}
            {apps.map((a) => (
              <TableRow key={a.application_id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`app-row-${a.application_id}`}>
                <TableCell>
                  <button
                    className="text-left text-slate-100 hover:text-emerald-300"
                    onClick={() => nav(`/broker/applications/${a.application_id}`)}
                    data-testid={`app-name-link-${a.application_id}`}
                  >
                    {a.borrower_name}
                  </button>
                  <div className="text-xs text-slate-500">{a.borrower_email}</div>
                </TableCell>
                <TableCell className="max-w-xs truncate text-slate-300">{a.property_address}</TableCell>
                <TableCell className="text-slate-300">{LOAN_TYPE_LABELS[a.loan_type] || a.loan_type}</TableCell>
                <TableCell><AiScoreBadge score={a.ai_score} testId={`app-score-${a.application_id}`} /></TableCell>
                <TableCell><StatusBadge status={a.status} testId={`app-status-${a.application_id}`} /></TableCell>
                <TableCell>
                  <span className="rounded-full border border-slate-700 bg-slate-950 px-2 py-0.5 text-[11px] text-slate-300">
                    {a.submitted_by === "sponsor" ? "Broker Submitted" : "Borrower"}
                  </span>
                </TableCell>
                <TableCell className="text-slate-400 text-sm">{fmtDate(a.created_at)}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => requestDocs(a.application_id)}
                    className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800"
                    data-testid={`app-prequal-${a.application_id}`}
                  >
                    <Send className="mr-1 h-3 w-3" /> Pre-Qualify
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => nav(`/broker/applications/${a.application_id}`)}
                    className="bg-emerald-600 text-white hover:bg-emerald-500"
                    data-testid={`app-view-${a.application_id}`}
                  >
                    <Eye className="mr-1 h-3 w-3" /> View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
